import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable, switchMap, take } from 'rxjs';
import { Store } from '@ngrx/store';
import { IvvaStore } from '../../store/store.model';
import { Offer } from './offer.model';
import { List } from '../../shared/list.model';
import { convertFromCrmId, convertToCrmId } from '../crm/crm.config';
import { convertUrl, Facility, InsideOutside, OffersType, OvernightStay, PaymentOption, ProductType } from '@toevla-webapplications/core';
import { LocationType } from '../locations/location-type.enum';
import { Condition } from './condition.model';
import { Theme } from './theme.model';
import { OFFERS_INITIAL_STATE } from '../../store/offers/offers.model';
import { ThemeType } from './theme-type.enum';
import { ConditionType } from './condition-type.enum';
import { PaymentMethod } from './payment-method.enum';

@Injectable({
	providedIn: 'root',
})
export class OffersService {
	private readonly http = inject(HttpClient);
	private readonly store = inject(Store<IvvaStore>);
	private readonly CRM_KEYS = ['destinations', 'experiences', 'facilities', 'impairments', 'meals', 'periods', 'roomtypes', 'targetAudiences', 'themes', 'transportations'];
	private readonly NEARBY_DISTANCE_KM = 20;

	getMaxPrices(): Observable<any> {
		return this.http.get<Offer[]>('/rest/offers/highestPrices').pipe(
			map((offers) => {
				const daytrip = offers.find((o) => o.type.toUpperCase() === OffersType.DAYTRIP);
				const accommodation = offers.find((o) => o.type.toUpperCase() === OffersType.ACCOMMODATION);

				return {
					maxPriceAccommodation: accommodation ? Math.ceil(accommodation.pricePeakseason || 999) : 999,
					maxPriceDaytrip: daytrip ? Math.ceil(daytrip.price || 999) : 999,
				};
			})
		);
	}

	getOffers(): Observable<List<Offer>> {
		return this.store.select('offers').pipe(
			take(1),
			switchMap((offers) => {
				let sortBy = offers.order.sort;
				const filters = this.convertFilters(offers.filters);

				if (sortBy === 'price') {
					if (offers.type === OffersType.ACCOMMODATION) {
						sortBy = 'pricePeakseason';
					}

					// filters.pricePublished = true;
				}

				const body: any = {
					fieldValues: {
						type: offers.type,
						...filters,
					},
					filters: {},
					minPrice: offers.filters.minPrice,
					maxPrice: offers.filters.maxPrice,
					sortBy,
					sortOrder: offers.order.asc ? 'asc' : 'desc',
					page: offers.data.page,
					size: offers.data.size,
				};

				if (offers.data.view === 'map') {
					body.xcoordinateWgs84 = OFFERS_INITIAL_STATE.map.lat;
					body.ycoordinateWgs84 = OFFERS_INITIAL_STATE.map.long;
					body.radius = 400;
					body.sortBy = 'distance';
					body.sortOrder = 'ASC';
					body.size = 400;
					delete body.fieldValues.pricePublished;
				}

				return this.http.post<{ data: Offer[]; total: number }>('/rest/offers/search', body);
			}),
			map((res) => {
				return {
					data: res.data.map((offer: any) => this.convertOffer(offer)),
					total: res.total,
				};
			})
		);
	}

	getOffersByName(name: string): Observable<Partial<Offer>[]> {
		return this.store.select('offers', 'type').pipe(
			take(1),
			switchMap((type) => {
				const body: any = {
					fieldValues: {
						type,
						name,
					},
					filters: {},
					sortBy: 'name',
					sortOrder: 'asc',
					page: 0,
					size: 10,
				};

				return this.http.post<{ data: Offer[]; total: number }>('/rest/offers/search', body);
			}),
			map((res) => {
				return res.data.map((offer) => {
					return {
						id: offer.id,
						name: offer.name,
					};
				});
			})
		);
	}

	getOffersNearby(type: OffersType, lat: number, long: number, withFilters: boolean = false): Observable<Offer[]> {
		const body = {
			fieldValues: {
				type,
			},
			xcoordinateWgs84: lat,
			ycoordinateWgs84: long,
			radius: this.NEARBY_DISTANCE_KM,
			sortBy: 'distance',
			sortOrder: 'ASC',
			page: 0,
			size: 5,
		};

		return this.store.select('offers').pipe(
			take(1),
			switchMap((offers) => {
				if (withFilters) {
					const filters = this.convertFilters(offers.filters);
					body.fieldValues = {
						...body.fieldValues,
						...filters,
					};
				}

				return this.http.post<{ data: Offer[]; total: number }>('/rest/offers/search', body);
			}),
			map((res) => {
				return res.data.map((offer: any) => this.convertOffer(offer));
			})
		);
	}

	getOffer(id: string): Observable<Offer> {
		return this.http.get<any>(`/rest/offers/${id}`).pipe(map((res: any) => this.convertOffer(res)));
	}

	private convertFilters(filters: any): any {
		const obj: any = {};

		if (filters.location) {
			switch (filters.location.type) {
				case LocationType.CITY:
					obj.city = filters.location.data.city;
					break;
				case LocationType.PROVINCE:
					obj.province = filters.location.data;
					break;
				case LocationType.REGION:
					obj.region = filters.location.data;
					break;
			}
		}

		if (filters.productTypes?.length) {
			obj.subtype = this.getProductTypes(filters.productTypes);
		}

		if (filters.paymentMethod?.length === 1) {
			if (filters.paymentMethod[0] === PaymentOption.PAYMENT_UPFRONT) {
				obj.paymentMethod = [PaymentMethod.YES_TARGET_AUDIENCE, PaymentMethod.NO];
			} else if (filters.paymentMethod[0] === PaymentOption.PAYMENT_ON_ARRIVAL) {
				obj.paymentMethod = [PaymentMethod.YES_EVERYONE];
			}
		}

		if (filters.insideOutside?.length) {
			if (filters.insideOutside.find((io: InsideOutside) => io === InsideOutside.INSIDE)) {
				obj.indoor = true;
			}

			if (filters.insideOutside.find((io: InsideOutside) => io === InsideOutside.OUTSIDE)) {
				obj.outdoor = true;
			}
		}

		if (filters.overnightStays?.length) {
			if (filters.overnightStays.find((os: OvernightStay) => os === OvernightStay.WITH_OVERNIGHT_STAY)) {
				obj.overnightPossible = true;
			}

			if (filters.overnightStays.find((os: OvernightStay) => os === OvernightStay.WITHOUT_OVERNIGHT_STAY)) {
				obj.overnightNotPossible = true;
			}
		}

		for (const key of this.CRM_KEYS) {
			if (filters[key]?.length) {
				let list = filters[key];

				if (key === 'facilities') {
					list = this.getFacilities(filters[key]);
				} else if (key === 'themes') {
					list = this.getThemes(filters[key]);
				}

				obj[key] = list.map((item: string | string[]) => {
					if (Array.isArray(item)) {
						return item.map((inner) => ({ id: convertToCrmId(inner) }));
					}

					return { id: convertToCrmId(item) };
				});
			}
		}

		const conditions = [];

		if (filters.exclusive?.length) {
			const exclusiveConditions = this.getConditions(filters.exclusive);

			const matrix = exclusiveConditions.map((ex) => {
				if (Array.isArray(ex)) {
					return ex.map((inner) => ({ id: convertToCrmId(inner), exclusive: true }));
				}

				return { id: convertToCrmId(ex), exclusive: true };
			});

			conditions.push(...matrix);
		}

		if (filters.inclusive?.length) {
			const inclusiveConditions = this.getConditions(filters.inclusive);

			const matrix = inclusiveConditions.map((ex) => {
				if (Array.isArray(ex)) {
					return ex.map((inner) => ({ id: convertToCrmId(inner), inclusive: true }));
				}

				return { id: convertToCrmId(ex), inclusive: true };
			});

			conditions.push(...matrix);
		}

		if (conditions.length) {
			obj.conditions = conditions;
		}

		return obj;
	}

	private convertConditions(conditions: any[], type: 'exclusive' | 'inclusive'): Condition[] {
		const parents: Condition[] = [];

		conditions.forEach((c) => {
			if (c[type]) {
				if (c.parentConditionId) {
					const index = parents.findIndex((p) => p.id === c.parentConditionId);

					if (index >= 0) {
						parents[index].conditions?.push({
							id: c.id,
							name: c.name,
						});
					} else {
						parents.push({
							id: c.parentConditionId,
							name: c.parentConditionName,
							conditions: [
								{
									id: c.id,
									name: c.name,
								},
							],
						});
					}
				} else {
					const index = parents.findIndex((p) => p.id === c.id);

					if (index < 0) {
						parents.push({
							id: c.id,
							name: c.name,
							conditions: [],
						});
					}
				}
			}
		});

		return parents;
	}

	private convertOffer(offer: any): Offer {
		let mainIndex = offer.images.findIndex((img: any) => img.main);

		if (mainIndex < 0) {
			mainIndex = 0;
		}

		const mainImage = offer.images[mainIndex];

		const obj = {
			...offer,
			images: offer.images.filter((img: any) => img.id !== mainImage.id),
			poster: mainImage,
		};

		for (const key of this.CRM_KEYS) {
			if (offer[key] && offer[key].length) {
				obj[key] = offer[key].map((item: any) => convertFromCrmId(item.id)).filter((item: any) => item);
			} else {
				obj[key] = [];
			}
		}

		if (offer.rentalunits) {
			obj.rentalunits = offer.rentalunits.map((unit: any) => ({
				...unit,
				type: convertFromCrmId(unit.typeId),
			}));
		}

		if (offer.type === OffersType.ORGANIZED_HOLIDAY) {
			obj.exclusiveConditions = this.convertConditions(offer.conditions || [], 'exclusive');
			obj.inclusiveConditions = this.convertConditions(offer.conditions || [], 'inclusive');
			obj.themes = this.convertThemes(offer.themes || []);

			delete obj.conditions;
		}

		obj.checkOfferUrl = offer.checkOfferUrl ? convertUrl(offer.checkOfferUrl) : null;
		obj.website = convertUrl(offer.website);

		return obj;
	}

	private getConditions(list: string[]): matrix<string> {
		const conditions = [...list];
		this.replaceValueWithList(conditions, ConditionType.BEHAVIOUR, [ConditionType.BEHAVIOUR, 'ce926c8a-7c21-ef11-840b-002248999d25', 'd0926c8a-7c21-ef11-840b-002248999d25']);
		this.replaceValueWithList(conditions, ConditionType.BOYS, [ConditionType.BOYS]);
		this.replaceValueWithList(conditions, ConditionType.DEV_DISORDER, [
			ConditionType.DEV_DISORDER,
			'6c725569-7c21-ef11-840a-0022489b516f',
			'ca926c8a-7c21-ef11-840b-002248999d25',
			'c8926c8a-7c21-ef11-840b-002248999d25',
			'cc926c8a-7c21-ef11-840b-002248999d25',
		]);
		this.replaceValueWithList(conditions, ConditionType.FYSICAL, [
			ConditionType.FYSICAL,
			'fa4d9c54-7c21-ef11-840a-0022489b516f',
			'9bfa125b-7c21-ef11-840a-0022489b516f',
			'e27472d9-591f-ef11-840a-000d3ade305e',
			'014e9c54-7c21-ef11-840a-0022489b516f',
			'dc926c8a-7c21-ef11-840b-002248999d25',
		]);
		this.replaceValueWithList(conditions, ConditionType.GIRLS, [ConditionType.GIRLS]);
		this.replaceValueWithList(conditions, ConditionType.MENTAL_DISABILITY, [ConditionType.MENTAL_DISABILITY]);
		this.replaceValueWithList(conditions, ConditionType.SPECIFIC, [
			ConditionType.SPECIFIC,
			'da926c8a-7c21-ef11-840b-002248999d25',
			'd8926c8a-7c21-ef11-840b-002248999d25',
			'e0926c8a-7c21-ef11-840b-002248999d25',
			'de926c8a-7c21-ef11-840b-002248999d25',
			'e4926c8a-7c21-ef11-840b-002248999d25',
			'e2926c8a-7c21-ef11-840b-002248999d25', // Gedragsproblemen
		]);
		this.replaceValueWithList(conditions, ConditionType.VULNERABILITY, [
			ConditionType.VULNERABILITY,
			'd2926c8a-7c21-ef11-840b-002248999d25',
			'd6926c8a-7c21-ef11-840b-002248999d25',
			'd4926c8a-7c21-ef11-840b-002248999d25',
		]);
		return conditions;
	}

	private getFacilities(list: string[]): matrix<string> {
		const facilities = [...list];
		this.replaceValueWithList(facilities, 'ALL_INDOOR_POOLS', [Facility.INDOOR_POOL_SEASONAL, Facility.INDOOR_POOL_ALL_YEAR]);
		this.replaceValueWithList(facilities, 'ALL_OUTDOOR_POOLS', [Facility.OUTDOOR_POOL_SEASONAL, Facility.OUTDOOR_POOL_ALL_YEAR, Facility.SWIMMING_POND]);
		this.replaceValueWithList(facilities, 'ALL_BIKES', [Facility.BIKES_FREE, Facility.BIKES_PAID]);
		this.replaceValueWithList(facilities, 'ALL_SHUTTLE_SERVICES', [Facility.SHUTTLE_SERVICE_FREE, Facility.SHUTTLE_SERVICE_PAID]);
		return facilities;
	}

	private getProductTypes(list: string[]): string[] {
		const productTypes = [...list];
		this.replaceValueWithList(productTypes, 'CAMPING_AND_TERRAIN', ProductType.CAMPING, ProductType.GENERIC_TERRAIN);
		this.replaceValueWithList(productTypes, 'HOTEL_AND_ROOMS', ProductType.HOTEL, ProductType.GENERIC_ROOMS);
		return productTypes;
	}

	private getThemes(list: string[]): matrix<string> {
		const themes = [...list];
		this.replaceValueWithList(themes, ThemeType.ACTION, [
			ThemeType.ACTION,
			'53d3ca86-591f-ef11-840b-002248999d25',
			'59d3ca86-591f-ef11-840b-002248999d25',
			'55d3ca86-591f-ef11-840b-002248999d25',
			'57d3ca86-591f-ef11-840b-002248999d25',
			'27164793-d02f-ef11-840a-002248a0ac8a',
			'5bd3ca86-591f-ef11-840b-002248999d25',
			'7ae27118-7a21-ef11-840a-000d3ade305e',
			'fad17fad-7a21-ef11-840a-000d3ade305e',
			'5dd3ca86-591f-ef11-840b-002248999d25',
		]);
		this.replaceValueWithList(themes, ThemeType.ANIMALS, [
			ThemeType.ANIMALS,
			'35f49655-7b21-ef11-840a-000d3ade305e',
			'f0f39655-7b21-ef11-840a-000d3ade305e',
			'b68e9e0c-799b-ef11-8a6a-7c1e523756c2',
		]);
		this.replaceValueWithList(themes, ThemeType.ANIMATOR, [ThemeType.ANIMATOR]);
		this.replaceValueWithList(themes, ThemeType.CREATIVE, [
			ThemeType.CREATIVE,
			'96b2ffc2-581f-ef11-840b-0022489996d0',
			'92b2ffc2-581f-ef11-840b-0022489996d0',
			'a2b2ffc2-581f-ef11-840b-0022489996d0',
			'8615d712-661e-ef11-840a-000d3ade305e',
			'8ab2ffc2-581f-ef11-840b-0022489996d0',
			'8cb2ffc2-581f-ef11-840b-0022489996d0',
			'a4b2ffc2-581f-ef11-840b-0022489996d0',
			'90b2ffc2-581f-ef11-840b-0022489996d0',
			'98b2ffc2-581f-ef11-840b-0022489996d0',
			'9eb2ffc2-581f-ef11-840b-0022489996d0',
			'94b2ffc2-581f-ef11-840b-0022489996d0',
			'9ab2ffc2-581f-ef11-840b-0022489996d0',
			'8eb2ffc2-581f-ef11-840b-0022489996d0',
			'a0b2ffc2-581f-ef11-840b-0022489996d0',
			'9cb2ffc2-581f-ef11-840b-0022489996d0',
		]);
		this.replaceValueWithList(themes, ThemeType.DIGITAL, [
			ThemeType.DIGITAL,
			'1ce3dee3-7b21-ef11-840a-0022489b516f',
			'1631e4dd-7b21-ef11-840a-0022489b516f',
			'05bda6d7-7b21-ef11-840a-0022489b516f',
			'2031e4dd-7b21-ef11-840a-0022489b516f',
		]);
		this.replaceValueWithList(themes, ThemeType.GAMES, [
			ThemeType.GAMES,
			'a9b443be-7b21-ef11-840a-000d3ade305e',
			'70532fb2-7b21-ef11-840a-000d3ade305e',
			'98532fb2-7b21-ef11-840a-000d3ade305e',
			'b9015bc4-7b21-ef11-840a-000d3ade305e',
			'14b443be-7b21-ef11-840a-000d3ade305e',
		]);
		this.replaceValueWithList(themes, ThemeType.LANGUAGE, [ThemeType.LANGUAGE, '8d69429f-7b21-ef11-840a-000d3ade305e', '401d2293-7b21-ef11-840a-000d3ade305e']);
		this.replaceValueWithList(themes, ThemeType.MUSIC, [
			ThemeType.MUSIC,
			'71e9b129-7b21-ef11-840a-000d3ade305e',
			'85a73511-7b21-ef11-840a-000d3ade305e',
			'28756217-7b21-ef11-840a-000d3ade305e',
			'927b8cfd-7a21-ef11-840a-000d3ade305e',
			'88fe741d-7b21-ef11-840a-000d3ade305e',
			'bfc17c4b-ab9a-ef11-8a6a-0022489a6b8a',
		]);
		this.replaceValueWithList(themes, ThemeType.OTHER_INTERESTS, [
			ThemeType.OTHER_INTERESTS,
			'8e8f6ef1-7a21-ef11-840a-000d3ade305e',
			'249990d8-7a21-ef11-840a-000d3ade305e',
			'9e8f6ef1-7a21-ef11-840a-000d3ade305e',
			'029990d8-7a21-ef11-840a-000d3ade305e',
			'9981e3de-7a21-ef11-840a-000d3ade305e',
			'63905ce5-7a21-ef11-840a-000d3ade305e',
		]);
		this.replaceValueWithList(themes, ThemeType.REST, [
			ThemeType.REST,
			'5b49d080-7b21-ef11-840a-000d3ade305e',
			'7149d080-7b21-ef11-840a-000d3ade305e',
			'ed582a87-7b21-ef11-840a-000d3ade305e',
			'01fad77a-7b21-ef11-840a-000d3ade305e',
			'4d49d080-7b21-ef11-840a-000d3ade305e',
		]);
		this.replaceValueWithList(themes, ThemeType.SCIENCE, [
			ThemeType.SCIENCE,
			'cfe4d075-7b21-ef11-840b-002248999d25',
			'd1e4d075-7b21-ef11-840b-002248999d25',
			'11f3a861-7b21-ef11-840a-000d3ade305e',
			'd3e4d075-7b21-ef11-840b-002248999d25',
			'cde4d075-7b21-ef11-840b-002248999d25',
			'cbe4d075-7b21-ef11-840b-002248999d25',
		]);
		this.replaceValueWithList(themes, ThemeType.SPORT, [
			ThemeType.SPORT,
			'0334dc48-7b21-ef11-840b-002248999d25',
			'0934dc48-7b21-ef11-840b-002248999d25',
			'1934dc48-7b21-ef11-840b-002248999d25',
			'0d34dc48-7b21-ef11-840b-002248999d25',
			'1334dc48-7b21-ef11-840b-002248999d25',
			'1534dc48-7b21-ef11-840b-002248999d25',
			'0134dc48-7b21-ef11-840b-002248999d25',
			'1134dc48-7b21-ef11-840b-002248999d25',
			'0534dc48-7b21-ef11-840b-002248999d25',
			'0f34dc48-7b21-ef11-840b-002248999d25',
			'0b34dc48-7b21-ef11-840b-002248999d25',
			'0734dc48-7b21-ef11-840b-002248999d25',
			'e2afa935-7b21-ef11-840a-000d3ade305e',
			'1734dc48-7b21-ef11-840b-002248999d25',
			'1b34dc48-7b21-ef11-840b-002248999d25',
		]);
		return themes;
	}

	private convertThemes(themes: any[]): Theme[] {
		const parents: Theme[] = [];

		themes.forEach((c) => {
			if (c.parentThemeId) {
				const index = parents.findIndex((p) => p.id === c.parentThemeId);

				if (index >= 0) {
					parents[index].themes?.push({
						id: c.id,
						name: c.name,
					});
				} else {
					parents.push({
						id: c.parentThemeId,
						name: c.parentThemeName,
						themes: [
							{
								id: c.id,
								name: c.name,
							},
						],
					});
				}
			} else {
				const index = parents.findIndex((p) => p.id === c.id);

				if (index < 0) {
					parents.push({
						id: c.id,
						name: c.name,
						themes: [],
					});
				}
			}
		});

		return parents;
	}

	private replaceValueWithList(list: (string | string[])[], key: string, ...items: matrix<string>) {
		const index = list.indexOf(key);

		if (index !== -1) {
			list.splice(index, 1, ...items);
		}
	}
}

type matrix<T> = (T | T[])[];
